import { FormArray, FormControl, UntypedFormGroup } from '@angular/forms';
import { isNil } from 'lodash';
import { TreeElement } from 'mbs-ui-kit';
import { AzureAccessTierType, StorageClass, TypesForFFI } from 'mbs-ui-shared@models';
import { GetBackupContentType, ItemTypeEnum, TreeBunchesIcons, TreeRestorePointsIconPath } from 'mbs-ui-shared@models/backup/storages-type';
import {
  cobbledDateFormat,
  dateTimePointsFormatWithAm,
  mediumDateWithTimeMoment,
  mediumDateWithoutTimeMoment
} from 'mbs-ui-shared@utils/date';
import moment from 'moment';
import { RestorePointItem } from 'wizards/models/restore-point-models';
import { AlternativeTreeIconPath, TreeIconPath } from 'wizards/models/what-backup-tree-model';
import { DayOfWeek } from '../models/DayOfWeek.enum';
import { WeekNumber } from '../models/WeekNumber.enum';

export type TreeRequestData = { data: { items: any[]; totalCount: number } };
export type StorageClassesArray = { value: StorageClass; label: string }[];
export type TreeData = { elements: TreeElement[]; indeterminate: boolean };
export type ExtendedTreeElement = TreeElement & {
  path?: string;
  isVersion?: boolean;
  isFolder?: boolean;
  parent?: ExtendedTreeElement;
  restorePath?: string;
};
export type GenerateTreeDataSettings = {
  rootTree?: TreeElement[];
  root: TreeElement;
  newInc: string[];
  newExc: string[];
  joinStr: string;
  sep: string;
  restorePath?: boolean;
  alt: boolean;
  onlyOneSelect?: boolean;
  hideFileType?: boolean;
  isUnix: boolean;
  needSlice?: boolean;
  isManually?: boolean;
  check?: boolean;
  disableCheckbox?: boolean;
  isVirtual?: boolean;
  needHide?: (item: any) => boolean;
  isVMOrDB?: boolean;
  isCbfVM?: boolean;
  isHyperV?: boolean;
  isProxmox?: boolean;
};

export class StepsHelpers {
  public static findStrForGetSubtree(strings: string[], path: string, separator: string, isUnix = false, needTest = false): string {
    const itemPathLen = path.split(separator).length;
    if (isUnix) {
      const equal = strings.find((str) => str === path);
      return equal || strings.find((str) => str.includes(path) && str.split(separator).length > itemPathLen);
    } else {
      const lowPath = path.toLowerCase();
      const equal = strings.find((str) => {
        const lowStr = needTest && str.startsWith(separator) ? str.substring(1) : str.toLowerCase();
        return lowStr === lowPath;
      });
      return (
        equal ||
        strings.find((str) => {
          const lowStr = needTest && str.startsWith(separator) ? str.substring(1) : str.toLowerCase();
          return !this.pathNotIncludePath(isUnix, separator, lowStr, lowPath) && str.split(separator).length > itemPathLen;
        })
      );
    }
  }

  public static getSortedTreeItems(items: any[], alt: boolean): any[] {
    const files: any[] = [];
    const folders: any[] = [];
    items.forEach((a) => {
      if (alt ? StepsHelpers.getDiskType(a, alt) === 'File' : StepsHelpers.getDiskType(a, alt) === 0) files.push(a);
      else folders.push(a);
    });
    folders.push(...files);
    return folders;
  }

  public static getDiskType(item, isAlt = false): string | number {
    return isAlt ? item.type || 'Disk' : +item.type === 3 ? 0 : +item.type;
  }

  public static getGotChildren(settings: GenerateTreeDataSettings, item: any, type: string | number): boolean {
    if (settings.isVMOrDB) {
      return !settings.isManually && !settings.isCbfVM
        ? item.itemType === ItemTypeEnum.Machine || item.itemType === ItemTypeEnum.Disk || item.itemType === ItemTypeEnum.DatabaseSql
        : item.itemType === ItemTypeEnum.RestorePoint;
    }

    return !settings.isManually ? (settings.alt ? !type || type === 'File' : !+type || +type === 3) : type === 'Version';
  }

  public static getIconFromTypeForVMOrDB(settings: GenerateTreeDataSettings, item: any): string {
    if (settings.isProxmox) return TreeBunchesIcons.Proxmox;

    const type = item.itemType || item.type;

    if (type === ItemTypeEnum.DatabaseSql) return TreeRestorePointsIconPath.DBPlan;
    if (type === ItemTypeEnum.RestorePoint) return item.isFull ? TreeRestorePointsIconPath.Full : TreeRestorePointsIconPath.Diff;

    return type === ItemTypeEnum.Disk
      ? TreeRestorePointsIconPath.Disk
      : settings.isHyperV
        ? TreeBunchesIcons.HyperV
        : TreeBunchesIcons.VmWare;
  }

  public static getIconFromTypeForUnix(type: number): string {
    return type === ItemTypeEnum.Folder ? TreeIconPath.Folder : AlternativeTreeIconPath[0];
  }

  public static getIconFromType(type: string | number, isAlt: boolean): string {
    switch (type) {
      case GetBackupContentType.Disk:
        return TreeRestorePointsIconPath.ParentDisk;
      case GetBackupContentType.Volume:
        return TreeRestorePointsIconPath.Disk;
      case GetBackupContentType.Version:
        return TreeRestorePointsIconPath.File;
      default:
        if (isAlt) return TreeIconPath[type];

        return type === 1 ? TreeIconPath.Folder : AlternativeTreeIconPath[+type];
    }
  }

  public static getPathFromItem(item, joinStr: string, isRestorePath = false, isUnix = false): string {
    if (item.type === GetBackupContentType.Version) {
      const path = item.path.split(joinStr);
      path[path.length - 1] = item.modifyDateUTC ? this.getNormalDate(item.modifyDateUTC) : path[path.length - 1];

      return path.join(joinStr);
    }

    let restorePath = item.restorePath;

    if (isRestorePath && restorePath && !/([a-zA-Z]:)/.test(restorePath) && restorePath.startsWith(joinStr + joinStr)) {
      restorePath = restorePath.substring(2);
    }

    return isRestorePath ? (isUnix ? (item as any).path.split(joinStr).slice(5).join(joinStr) : restorePath || item.path) : item.path;
  }

  public static generateDisksTreeData(data: any, excludes: string[], settings: GenerateTreeDataSettings): ExtendedTreeElement[] {
    return data.data.items.map((item, idx) => {
      const pathArray = item.path.split(settings.joinStr);
      const newPath = settings?.needSlice && pathArray?.length > 5 ? pathArray.slice(5).join(settings.joinStr) : item.path;
      const existExc = excludes.some((exc) => this.equalStrings(exc, newPath, settings.isUnix));
      const newTreeItem = this.getNewTreeItem(item, idx, !existExc, false, settings);
      newTreeItem.restorePath = newPath;
      return newTreeItem;
    });
  }

  public static generatePointsTreeData(
    data: any,
    folders: string[],
    point: string,
    settings: GenerateTreeDataSettings
  ): ExtendedTreeElement[] {
    return data.data.items.map((item, idx) => {
      const pathArray = item.path.split(settings.joinStr);
      const newPath = pathArray?.length > 5 ? pathArray.slice(3).join(settings.joinStr) : item.path;
      const checked = point && item.path.endsWith(point);
      const newTreeItem = this.getNewTreeItem(item, idx, checked, false, settings);
      newTreeItem.restorePath = newPath;
      return newTreeItem;
    });
  }

  public static generateDBTreeData(data: any, formArray: string[], settings: GenerateTreeDataSettings): ExtendedTreeElement[] {
    return data.data.items.map((item, idx) => {
      let path = (item as any).path.split(settings.joinStr).slice(3).join(settings.joinStr);
      const index = (settings.root.label as string).indexOf(settings.joinStr);

      if (index !== -1 && path[index] !== settings.joinStr) {
        path = path.split('');
        path[index] = settings.joinStr;
        path = path.join('');
      }

      const checked = formArray.some((str) => this.equalStrings(str, path, false));
      return this.getNewTreeItem(item, idx, checked, false, settings, path);
    });
  }

  public static generateTreeData(
    data: any,
    formArray: string[],
    excFormArray: string[],
    settings: GenerateTreeDataSettings
  ): ExtendedTreeElement[] {
    // eslint-disable-next-line sonarjs/cognitive-complexity
    const newElements = data.data.items.map((item, idx) => {
      const myPath = this.getPathFromItem(item, settings.joinStr, settings.restorePath, settings.isUnix);
      const itemPathLen = myPath.length;
      const existStr = this.findStrForGetSubtree(formArray, myPath, settings.joinStr, settings.isUnix, settings.isVirtual);
      const existLen = existStr ? existStr.length : 0;
      const existInExclude = this.findStrForGetSubtree(excFormArray, myPath, settings.joinStr, settings.isUnix, settings.isVirtual);
      const childExclude = !!existInExclude && existInExclude.length > itemPathLen;
      const checked = settings.onlyOneSelect
        ? !!existStr || !this.pathNotIncludePath(settings.isUnix, settings.joinStr, myPath, formArray[0])
        : (!!existStr && existLen === itemPathLen) ||
          (item.type !== 'Version' && (!existInExclude || childExclude) && settings.root.checked);
      const forIndeterminate = childExclude && (checked || settings.root.checked || this.someParentIsChecked(settings.root));
      let indeterminate = (!!existStr && existStr.length > itemPathLen) || forIndeterminate;
      if (settings.isUnix && this.isChildRoot(settings.root, settings.joinStr) && settings.newInc.length && settings.newExc.length) {
        const incStrForSubtree = this.findStrForGetSubtree(settings.newInc, myPath, settings.joinStr, settings.isUnix, settings.isVirtual);
        const excStrForSubtree = this.findStrForGetSubtree(settings.newExc, myPath, settings.joinStr, settings.isUnix, settings.isVirtual);
        indeterminate =
          indeterminate &&
          ((settings.newInc.length && incStrForSubtree && incStrForSubtree[myPath.length] === settings.joinStr) ||
            (forIndeterminate && settings.newExc.length && excStrForSubtree && excStrForSubtree[myPath.length] === settings.joinStr));
      }

      return this.getNewTreeItem(item, idx, checked, indeterminate, settings);
    });
    this.addInvisibleItemsToTree(newElements, settings, formArray, excFormArray);
    return newElements;
  }

  public static oldGenerateTreeData(data: any, formArray: string[], separator: string, isManually = false): TreeData {
    let childChecked: boolean;
    let indeterminate: boolean;

    const newElements = data.d.map((item, idx) => {
      const newElement = {
        label: item.Name,
        id: `${idx}${separator}${item.Path}`,
        checked: formArray.some((str) => str.includes(item.Path)),
        icon: AlternativeTreeIconPath[!item.Type && item.Type !== 0 ? 3 : item.Type !== 1 ? item.Type : 0],
        gotChildren: !isManually ? !item.Type : item.Type === 1,
        expanded: false
      };
      if (indeterminate === null || indeterminate === undefined) {
        if (childChecked === null || childChecked === undefined) childChecked = newElement.checked;
        if (childChecked !== newElement.checked) indeterminate = true;
      }
      return newElement;
    });

    return { elements: newElements, indeterminate: indeterminate };
  }

  public static getPathInStringFormat(root: TreeElement): string {
    const arrayFolders = this.getArrayOfPath(root);
    if (arrayFolders.length > 1) {
      const idx = arrayFolders.length - 1;
      if (root.icon === AlternativeTreeIconPath[0]) arrayFolders[0] = arrayFolders[0].slice(0, arrayFolders[0].length - 1); // TODO Del it for new tree endpoint...
      const newArray = arrayFolders.slice(0, idx);
      return arrayFolders[idx].toUpperCase() + newArray.reverse().join('');
    }
    return (root.label as string).toUpperCase();
  }

  public static getArrayOfPath(root: TreeElement): string[] {
    const result = [];
    result.push(`${root.label}\\`);
    if (root.parent) result.push(...this.getArrayOfPath(root.parent));
    return result;
  }

  public static getArrayOfPathByOs(root: ExtendedTreeElement, isUnix: boolean, isVirtual = false, notFloat = false): string[] {
    const result = [];
    if (isUnix && root.path) {
      result.push(root.path);
      if (notFloat) return result;
    } else {
      if (isVirtual || root.isVersion) {
        const pathArr = (root.path || (root as any).originalPath).split(isUnix ? '/' : '\\');
        result.push(pathArr[pathArr.length - 1]);
      } else {
        result.push(root.label);
      }
    }
    if (root.parent) {
      const parent = this.getArrayOfPathByOs(root.parent, isUnix, isVirtual);
      result.push(...parent);
    }
    return result;
  }

  public static toggleTreeElementsStatuses(element: TreeElement): void {
    if (element.children && element.children.length) {
      if (!element.children.some((c) => c.checked)) {
        if (element.indeterminate) {
          element.checked = true;
          element.children.forEach((e) => this.toggleTreeElementsStatuses(e));
        } else {
          element.checked = false;
          element.indeterminate = false;
        }
      } else {
        if (element.indeterminate) element.checked = true;
        element.children.forEach((e) => this.toggleTreeElementsStatuses(e));
      }
    }
  }

  public static getArrayOfStrings(event: TreeElement, separator: string, isFullPath = false): string[] {
    const result = [];
    if (event.checked) {
      const hasChildren = event.children && !!event.children.length;
      if (!isFullPath) {
        if (!event.indeterminate && (!hasChildren || !event.children.some((c) => !c.checked)))
          result.push(this.getStringFromTreeElement(event));
        else event.children.forEach((child) => result.push(...this.getArrayOfStrings(child, separator)));
      } else {
        if (hasChildren) event.children.forEach((child) => result.push(...this.getArrayOfStrings(child, separator, isFullPath)));
        else result.push(this.getStringFromTreeElement(event));
      }
    }
    return result;
  }

  public static getNormalDate(date: string): string {
    return moment.utc(date).format(cobbledDateFormat);
  }

  static getPathUnixOnWindows(path: string, join: string, compName: string, isNBF: boolean): string {
    if (!isNBF) {
      const newPathArray = path
        .split(join)
        .filter((s) => !!s)
        .slice(1);
      if (newPathArray?.length === 1) return newPathArray.join(join) + join;
      return newPathArray.join(join) || '';
    }

    let newStr = this.getRootPathWithJoin(path, join, false, compName);

    if (newStr.startsWith('CBB_') || newStr.startsWith('etc')) newStr += join;

    return newStr;
  }

  static getRootPathWithJoin(path: string, join: string, isUnix = false, replaceStr = ''): string {
    let newPath = !isUnix && path.length === 2 && /^([a-zA-Z]:)/.test(path) ? path + join : path;

    if (!replaceStr) return newPath;

    newPath = newPath.split(replaceStr + join)[1] || newPath.split(replaceStr + join)[0];

    if (newPath.startsWith('\\\\\\')) newPath = newPath.replace('\\\\\\', '');

    return newPath;
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public static getPathInStringFormatByOs(
    root: TreeElement,
    isUnix = false,
    reverse = false,
    isVirtual = false,
    notFloat = false,
    needFirst = false,
    oldFormat = false
  ): string {
    const arrayFolders = this.getArrayOfPathByOs(root, isUnix, isVirtual, notFloat);
    const join = isUnix ? '/' : '\\';

    if (arrayFolders.length > 1) {
      if (needFirst) return arrayFolders[0];
      if (reverse) {
        let result = arrayFolders.reverse().join(join);
        if (!result.startsWith(join, 0) && !isVirtual) result = join + result;
        return result;
      } else {
        const idx = arrayFolders.length - 1;
        const newArray = arrayFolders.slice(0, idx);
        return arrayFolders[idx] + newArray.reverse().join(join);
      }
    }

    return isUnix && !oldFormat
      ? (root as any).path.split(join).slice(5).join(join)
      : isVirtual || (isUnix && oldFormat)
        ? (root as any).path || (root as any).originalPath
        : (root.label as string);
  }

  public static uncheckAllParent(event: TreeElement, needOffIndeterminate = false, optionality = false, unixOnWindowsPC = false): void {
    event.checked = false;
    if (needOffIndeterminate) {
      event.indeterminate = optionality
        ? event.children && event.children.some((c) => (c.shown || !unixOnWindowsPC) && (c.checked || c.indeterminate))
        : false;
    }
    if (event.parent) this.uncheckAllParent(event.parent, needOffIndeterminate, optionality, unixOnWindowsPC);
  }

  public static findInNotRootFolder(strings: string[], treeData: TreeElement[], needOriginal = false): string[] {
    return needOriginal
      ? strings.filter((s) => !treeData.some((d) => (d as any).originalPath !== '/' && s.includes((d as any).originalPath)))
      : strings.filter((s) => !treeData.some((d) => (d as any).path !== '/' && s.includes((d as any).path)));
  }

  public static pathNotIncludePath(
    isUnix,
    separator: string,
    parentPath: string,
    childPath: string,
    parentArr = null,
    childArr = null
  ): boolean {
    const newParentPath = isUnix ? parentPath : parentPath?.toLowerCase() || '';
    const newChildPath = isUnix ? childPath : childPath?.toLowerCase() || '';

    if (!newParentPath.includes(newChildPath)) return true;

    if (!parentArr && !childArr) {
      return newParentPath[newChildPath.length - 1] !== separator && newParentPath[newChildPath.length] !== separator;
    }

    const newParentArr = parentArr || parentPath?.split(separator);
    const newChildArr = childArr || childPath?.split(separator);

    if (newParentArr && newParentArr.length === newChildArr?.length) return newParentPath !== newChildPath;

    return newParentPath[newChildPath.length - 1] !== separator && newParentPath[newChildPath.length] !== separator;
  }

  public static someParentIsChecked(event: TreeElement): boolean {
    if (event.parent) return event.parent.checked || this.someParentIsChecked(event.parent);
    else return event.checked;
  }

  public static isChildRoot(element: TreeElement, joinForInput: string): boolean {
    if (element.parent) return this.isChildRoot(element.parent, joinForInput);
    else return (element as any).path === joinForInput;
  }

  public static getRootPathForShare(path: string): string {
    const splitPath = path.split('\\').filter((str) => !!str);
    return `\\\\${splitPath.slice(0, 2).join('\\')}\\`;
  }
  public static equalStrings(first: string, second: string, isUnix: boolean): boolean {
    return isUnix ? first === second : first.toLowerCase() === second.toLowerCase();
  }

  public static toggleAllInTree(element: TreeElement, state = false, expanded = false): void {
    element.checked = state;
    element.indeterminate = false;
    element.expanded = expanded;
    if (element.children && element.children.length) element.children.forEach((el) => this.toggleAllInTree(el, state));
  }

  public static getStorageClasses(withGlacierIR?: boolean): StorageClassesArray {
    return [
      { value: StorageClass.STANDARD, label: 'Standard' },
      { value: StorageClass.STANDARD_IA, label: 'Standard-IA' },
      { value: StorageClass.INTELLIGENT_TIERING, label: 'Intelligent-Tiering' },
      { value: StorageClass.ONEZONE_IA, label: 'One Zone-IA' },
      { value: StorageClass.GLACIER_IR, label: 'Glacier Instant Retrieval' },
      { value: StorageClass.GLACIER, label: 'Glacier Flexible Retrieval' },
      { value: StorageClass.DEEP_ARCHIVE, label: 'Glacier Deep Archive' }
    ].filter((item) => {
      if (!withGlacierIR) {
        return item.value !== StorageClass.GLACIER_IR;
      }

      return true;
    });
  }

  public static isStorageBadForFFI(storage: StorageClass | AzureAccessTierType, type: 'Amazon' | 'Azure'): boolean {
    return (TypesForFFI.unsupported[type] as number[]).includes(storage);
  }

  public static getDefaultIncrementalSchedule(validVersionForSchedule = false): any {
    const formData: any = {
      recurringPeriod: new FormControl('Monthly'),
      weeklyDayOfWeek: new FormArray([]),
      occurrence: new FormControl(0),
      dayOfWeek: new FormControl(3),
      dayOfMonthCount: new FormControl(1),
      dailyFreqPeriodOption: new FormControl('OccursAt'),
      occursAtTime: new FormControl('00:00'),
      occursEveryCount: new FormControl(1),
      occursEveryPeriod: new FormControl('hours'),
      occursEveryFromTime: new FormControl('00:00'),
      occursEveryToTime: new FormControl('23:59')
    };
    if (validVersionForSchedule) {
      formData.repeatEveryCount = new FormControl(1);
      formData.startFromDate = new FormControl(new Date());
    }
    return formData;
  }

  public static getDefaultFullSchedule(validVersionForSchedule = false): any {
    const formData: any = {
      enabled: new FormControl(false),
      recurringPeriod: new FormControl('Monthly'),
      weeklyDayOfWeek: new FormArray([]),
      occurrence: new FormControl(0),
      dayOfWeek: new FormControl(3),
      dayOfMonthCount: new FormControl(1),
      dailyFreqPeriodOption: new FormControl('OccursAt'),
      occursAtTime: new FormControl('00:00')
    };
    if (validVersionForSchedule) {
      formData.repeatEveryCount = new FormControl(1);
      formData.startFromDate = new FormControl(new Date());
    }
    return formData;
  }

  public static updateDaysOfWeek(days: boolean[] = null, form: UntypedFormGroup, daysOfWeekEnumArray: any[] = []): void {
    const WeeklyDayOfWeek = form.get('weeklyDayOfWeek') as FormArray;
    if (days && days.length) {
      const len = WeeklyDayOfWeek.controls.length;
      for (let i = 0; i < len; i++) WeeklyDayOfWeek.removeAt(0);
      days.forEach((elem) => WeeklyDayOfWeek.push(new FormControl(elem)));
    } else {
      daysOfWeekEnumArray.forEach(() => WeeklyDayOfWeek.push(new FormControl(false)));
    }
  }

  public static getStringByScheduleData(schedule, needStartingFrom = true): string {
    let str = '';
    str = this.getStringIfSchedule(schedule);
    str += this.getStringPeriodOptions(schedule);
    if (needStartingFrom) str += ` starting from ${moment(schedule.StartFromDate).format(mediumDateWithoutTimeMoment)}`;
    return str;
  }

  public static getStringPeriodOptions(schedule): string {
    const atTime = moment(schedule.OccursAtTime, 'HH:mm').format('h:mm\u00A0A');
    const OccursEveryFromTime = moment(schedule.OccursEveryFromTime, 'HH:mm').format('h:mm\u00A0A');
    const OccursEveryToTime = moment(schedule.OccursEveryToTime, 'HH:mm').format('h:mm\u00A0A');
    return schedule.DailyFreqPeriodOption === 'OccursAt'
      ? 'at ' + atTime
      : `every ${schedule.OccursEveryCount} ${schedule.OccursEveryPeriod} from ${OccursEveryFromTime} to ${OccursEveryToTime}`;
  }

  public static getStringIfSchedule(schedule): string {
    let summary = 'Occurs every ';
    switch (schedule.RecurringPeriod) {
      case 'Daily':
        summary += `${schedule.RepeatEveryCount} day${schedule.RepeatEveryCount > 1 ? 's' : ''} `;
        break;
      case 'DayOfMonth':
        summary += this.getSummaryPartForDayOfMonth(schedule);
        break;
      case 'Monthly':
        summary +=
          schedule.Occurrence == WeekNumber['Day Of Month']
            ? this.getSummaryPartForDayOfMonth(schedule)
            : `${schedule.RepeatEveryCount} month${schedule.RepeatEveryCount > 1 ? 's' : ''} on ${WeekNumber[schedule.Occurrence]} ${
                DayOfWeek[schedule.DayOfWeek]
              } `;
        break;
      case 'Weekly': {
        const days: Array<string> = schedule.WeeklyDayOfWeek.map((x, i) => (x ? DayOfWeek[i] : '')).filter((x) => !!x);
        summary += `${schedule.RepeatEveryCount} week${schedule.RepeatEveryCount > 1 ? 's' : ''} on ${days.join(', ')} `;
        break;
      }
      case 'Yearly':
        summary += schedule.RepeatEveryCount + ' year' + (schedule.RepeatEveryCount > 1 ? 's' : '') + ' ';
        break;
    }
    return summary;
  }

  public static getPointLabel(p: RestorePointItem): string {
    const size = p.sizeOnStorage / 1024;

    return `${p.isFull ? 'Full' : 'Incremental'} — ${p.restorePointName} — ${
      size > 1024 ? this.getDiskSizeFromMB(size / 1024) : size.toFixed(2) + 'KB'
    } ${p.needDeepSync ? '— Need DeepSync' : ''}`;
  }

  public static getDiskSizeFromMB(mb: number, needRound = false): string {
    if (mb >= 1024) {
      const gb = mb / 1024;
      const isGB = gb < 1024;
      let result = isGB ? +(gb || 0.02).toFixed(2) : +(gb / 1024 || 0.02).toFixed(2);
      if (needRound) result = Math.ceil(result);
      return isGB ? result + ' GB' : result + ' TB';
    } else if (mb < 1) {
      return mb ? +(needRound ? Math.ceil(mb * 1024) : mb * 1024).toFixed(2) + ' KB' : '';
    } else return mb ? +(Math.ceil(mb) || 0.02).toFixed(2) + ' MB' : '';
  }

  public static getSummaryPartForDayOfMonth(schedule): string {
    return `${schedule.RepeatEveryCount} month${schedule.RepeatEveryCount > 1 ? 's' : ''} on ${schedule.DayOfMonthCount} day `;
  }

  public static updateTreeItemFromStringArray(newItem: TreeElement, strArray: string[], isUnix = false): void {
    newItem.checked = true;
    newItem.indeterminate = !!strArray.length;
    if (newItem.children && newItem.children.length && strArray.length) {
      if (isUnix) {
        newItem.children.forEach((el) => {
          if (el.label === strArray[0]) this.updateTreeItemFromStringArray(el, strArray.slice(1, strArray.length));
        });
      } else {
        newItem.children.forEach((el) => {
          if ((el.label as string).toLowerCase() === strArray[0].toLowerCase())
            this.updateTreeItemFromStringArray(el, strArray.slice(1, strArray.length));
        });
      }
    }
  }

  public static getTotalChildren(data: { totalCount?: number }, childrenLength: number): number {
    return data && isNil(data.totalCount) && childrenLength >= 300 ? Number.MAX_SAFE_INTEGER : (data?.totalCount || 0) - 1;
  }

  // Private Section
  private static getIndexOtherPath(
    strings: string[],
    elements: ExtendedTreeElement[],
    parentPath: string,
    settings: GenerateTreeDataSettings
  ): number {
    const parentNew = settings.isUnix ? parentPath : parentPath.toLowerCase();
    let newStrings = strings;
    if (settings.isUnix && settings.rootTree) {
      const rootStrings = StepsHelpers.findInNotRootFolder(strings, settings.rootTree);
      newStrings = this.isChildRoot(settings.root, settings.joinStr)
        ? rootStrings
        : Array.from(strings).filter((s) => !rootStrings.includes(s));
    }
    return newStrings.findIndex((s) => {
      const sNew = settings.isUnix ? s : s.toLowerCase();
      const idx = elements.findIndex((e) => {
        const eStr = settings.isUnix ? (e as any).path : (e as any).path.toLowerCase();
        return eStr === sNew || sNew.includes(eStr);
      });
      return !this.pathNotIncludePath(settings.isUnix, settings.joinStr, s, parentPath) && idx === -1 && sNew !== parentNew;
    });
  }

  private static getStringFromTreeElement(event: TreeElement & { path?: string }): string {
    return (event.path || event.label).toString();
  }

  private static getNewTreeItem(
    item,
    idx,
    checked,
    indeterminate,
    settings: GenerateTreeDataSettings,
    restorePath?: string
  ): ExtendedTreeElement {
    const type = this.getDiskType(item, settings.alt);
    const isVersion = type === GetBackupContentType.Version;
    const pathArr = isVersion ? item.path.split(settings.joinStr) : [];
    const displayDate = isVersion
      ? moment
          .utc(item.displayName, dateTimePointsFormatWithAm.test(item.displayName) ? 'M/D/YYYY HH:mm:ss A' : 'D.MM.YYYY HH:mm:ss')
          .format(mediumDateWithTimeMoment)
      : '';

    return {
      label:
        type === GetBackupContentType.Volume
          ? item.displayName.split(' ').slice(2).join(' ')
          : isVersion
            ? displayDate === 'Invalid date'
              ? moment.utc(pathArr[pathArr.length - 1], cobbledDateFormat).format(mediumDateWithTimeMoment) + ' (UTC)'
              : displayDate
            : item.displayName,
      id: `${idx}${settings.sep}${item.restorePath || item.path}`.replaceAll(' ', '-'),
      checked: checked || (indeterminate && type === GetBackupContentType.File),
      indeterminate: indeterminate && type !== GetBackupContentType.File && type !== GetBackupContentType.Version,
      icon: settings.isVMOrDB
        ? this.getIconFromTypeForVMOrDB(settings, item)
        : settings.isUnix && isNil(item.type) && !isNil(item.itemType)
          ? this.getIconFromTypeForUnix(item.itemType)
          : this.getIconFromType(type, settings.alt),
      gotChildren: this.getGotChildren(settings, item, type),
      expanded: false,
      shown: !settings.hideFileType || (item.itemType !== ItemTypeEnum.File && (settings.alt ? type !== 'File' : +type !== 0)),
      hideCheckbox: settings.needHide && settings.needHide(item),
      isVersion: isVersion,
      disableCheckbox: settings.disableCheckbox,
      isFolder: type === GetBackupContentType.Folder,
      path: isVersion ? pathArr.slice(0, pathArr.length - 1).join(settings.joinStr) + settings.joinStr + item.modifyDateUTC : item.path,
      restorePath:
        restorePath ||
        item?.restorePath ||
        (settings.isUnix ? (item as any).path.split(settings.joinStr).slice(5).join(settings.joinStr) : '')
    };
  }

  private static addInvisibleItemsToTree(
    newElements: ExtendedTreeElement[],
    settings: GenerateTreeDataSettings,
    mainFormArray: string[],
    excFormArray: string[]
  ): void {
    if (newElements.length) {
      const root = settings.root;
      const str = settings.isUnix ? (root as any).path : (((root as any).path || root.label) as string);
      const children = [...(root.children || []), ...newElements];
      const idx = this.getIndexOtherPath(mainFormArray, children, str, settings);
      const idxExc = this.getIndexOtherPath(excFormArray, children, str, settings);
      const includeInvisible = idx !== -1;
      if (includeInvisible || idxExc !== -1) {
        const path = includeInvisible ? mainFormArray[idx] : excFormArray[idxExc];
        newElements.push({
          label: path,
          id: `${idx}${settings.sep}${path}`,
          checked: includeInvisible,
          indeterminate: false,
          icon: '',
          gotChildren: false,
          expanded: false,
          shown: false,
          path: path
        });
      }
    }
  }
}
